import { NgClass } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { ConfirmationService, MessageService } from 'primeng/api';
import { Button } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { Tag } from 'primeng/tag';
import FirmwareRolloutDTO from '../../../../types/firmware-rollout/FirmwareRolloutDTO';
import RolloutPhase from '../../../../types/firmware-rollout/RolloutPhase';
import RolloutStatus from '../../../../types/firmware-rollout/RolloutStatus';
import RolloutStudioSummaryDTO from '../../../../types/firmware-rollout/RolloutStudioSummaryDTO';
import Page from '../../../../types/shared/Page';
import { EnumLabelPipe } from '../../pipes/enum-label.pipe';
import { FormatDateTime } from '../../pipes/format-date-time';
import { FirmwareRolloutService } from '../../services/firmware-rollout.service';
import { PageHeaderComponent } from '../shared/page-header/page-header.component';
import { TriggerFirmwareRolloutComponent } from './trigger-firmware-rollout/trigger-firmware-rollout.component';

const ACTIVE_STATUSES = [
    RolloutStatus.PENDING,
    RolloutStatus.IN_PROGRESS
];
const COMPLETED_STATUSES = [
    RolloutStatus.COMPLETED,
    RolloutStatus.COMPLETED_WITH_ERRORS,
    RolloutStatus.CANCELLED
];
const ACTIVE_ROLLOUTS_PAGE_SIZE = 50;
const POLL_INTERVAL_MS = 5000;

/** Shows active rollouts globally, or scoped to a studio when the route carries a studio :id. */
@Component({
    selector: 'app-firmware-rollout-dashboard',
    templateUrl: './firmware-rollout-dashboard.component.html',
    styleUrls: [ './firmware-rollout-dashboard.component.scss' ],
    imports: [
        PageHeaderComponent,
        Button,
        RouterLink,
        TableModule,
        Tag,
        FormatDateTime,
        NgClass,
        TriggerFirmwareRolloutComponent,
        EnumLabelPipe
    ]
})
export class FirmwareRolloutDashboardComponent implements OnInit, OnDestroy {

    protected readonly RolloutStatus = RolloutStatus;
    protected readonly RolloutPhase = RolloutPhase;

    public studioId: number | null = null;
    public studioSummary: RolloutStudioSummaryDTO | null = null;

    public activeRollouts: FirmwareRolloutDTO[] = [];
    public loadingActive = false;

    public completedRollouts: FirmwareRolloutDTO[] = [];
    public totalCompleted = 0;
    public rows = 10;
    public currentPage = 0;
    public loadingCompleted = false;
    public cancellingRolloutId: number | null = null;
    public cancellingAll = false;

    private pollHandle: ReturnType<typeof setInterval> | null = null;
    private polling = false;

    constructor(
        private route: ActivatedRoute,
        private firmwareRolloutService: FirmwareRolloutService,
        private messageService: MessageService,
        private confirmationService: ConfirmationService
    ) {
    }

    public ngOnInit(): void {
        const idParam = this.route.snapshot.paramMap.get('id');
        this.studioId = idParam != null ? Number(idParam) : null;
        this.loadSummary();
        this.loadActiveRollouts();
        this.loadCompletedRollouts();
        // Silent polling; skips a tick if the previous one is still in flight to avoid races.
        this.pollHandle = setInterval(() => {
            if (this.polling) {
                return;
            }
            this.polling = true;
            Promise.allSettled([
                this.loadSummary(),
                this.loadActiveRollouts(true),
                this.loadCompletedRollouts(true)
            ]).finally(() => {
                this.polling = false;
            });
        }, POLL_INTERVAL_MS);
    }

    public ngOnDestroy(): void {
        if (this.pollHandle != null) {
            clearInterval(this.pollHandle);
        }
    }

    public refreshAfterTrigger(): void {
        this.loadSummary();
        this.loadActiveRollouts();
        this.loadCompletedRollouts();
    }

    public onCompletedPageChange(event: any): void {
        this.currentPage = Math.floor(event.first / event.rows);
        this.rows = event.rows;
        this.loadCompletedRollouts();
    }

    /**
     * Percentage of samples that reached a terminal state (success or error).
     *
     * @param rollout The rollout to compute progress for.
     * @return The completion percentage (0-100).
     */
    public progressPercent(rollout: FirmwareRolloutDTO): number {
        const counts = rollout.sampleCounts;
        if (!counts || counts.total === 0) {
            return 0;
        }
        return Math.round(( ( counts.success + counts.error ) / counts.total ) * 100);
    }

    public phaseLabel(rollout: FirmwareRolloutDTO): string {
        switch (rollout.phase) {
            case RolloutPhase.PILOT_IN_PROGRESS:
                return `Flashing pilot ${ rollout.pilotMacAddress ?? '' }...`;
            case RolloutPhase.PILOT_FAILED:
                return `Pilot ${ rollout.pilotMacAddress ?? '' } failed - rollout stalled, needs attention`;
            case RolloutPhase.BATCH_IN_PROGRESS:
                return 'Flashing batch...';
            case RolloutPhase.AWAITING_DISPATCH:
                return 'Preparing rollout - transferring the firmware file to the Hub and waiting for it to confirm receipt...';
            default:
                return '';
        }
    }

    /**
     * Only stops future batches from being dispatched - a batch already broadcast to a sample can't be
     * recalled, so that sample still reports its real outcome even after the rollout is cancelled.
     */
    public cancelRollout(rollout: FirmwareRolloutDTO): void {
        this.confirmationService.confirm({
            header: 'Cancel Rollout',
            message: `Cancel this rollout targeting '${ rollout.targetReleaseVersion }' in `
                + `'${ rollout.studioName }'? Samples already mid-flash will still report `
                + `their real outcome - this only stops any batch not yet dispatched.`,
            icon: 'pi pi-exclamation-triangle',
            accept: () => this.submitCancel(rollout)
        });
    }

    /** Cancels every currently active rollout - handy after a global release to abort it everywhere at once. */
    public cancelAllActiveRollouts(): void {
        if (this.activeRollouts.length === 0) {
            return;
        }
        this.confirmationService.confirm({
            header: 'Cancel All Active Rollouts',
            message: `Cancel all ${ this.activeRollouts.length } currently active rollout(s)? Samples already `
                + `mid-flash will still report their real outcome - this only stops any batch not yet dispatched.`,
            icon: 'pi pi-exclamation-triangle',
            accept: () => this.submitCancelAll()
        });
    }

    private submitCancel(rollout: FirmwareRolloutDTO): void {
        this.cancellingRolloutId = rollout.id;
        this.firmwareRolloutService.cancelRollout(rollout.id)
            .then(() => {
                this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Rollout cancelled' });
                return Promise.allSettled([
                    this.loadActiveRollouts(true),
                    this.loadCompletedRollouts(true)
                ]);
            })
            .catch((error: any) => {
                this.messageService.add({ severity: 'error', summary: 'Error', detail: error.message });
            })
            .finally(() => {
                this.cancellingRolloutId = null;
            });
    }

    private submitCancelAll(): void {
        this.cancellingAll = true;
        const targets = this.activeRollouts;
        Promise.allSettled(targets.map(rollout => this.firmwareRolloutService.cancelRollout(rollout.id)))
            .then(results => {
                const failed = results.filter(result => result.status === 'rejected').length;
                if (failed === 0) {
                    this.messageService.add({
                        severity: 'success', summary: 'Success',
                        detail: `Cancelled ${ targets.length } rollout(s)`
                    });
                } else {
                    this.messageService.add({
                        severity: 'error', summary: 'Error',
                        detail: `Failed to cancel ${ failed } of ${ targets.length } rollout(s)`
                    });
                }
                return Promise.allSettled([
                    this.loadActiveRollouts(true),
                    this.loadCompletedRollouts(true)
                ]);
            })
            .finally(() => {
                this.cancellingAll = false;
            });
    }

    private loadSummary(): Promise<void> {
        if (this.studioId == null) {
            return Promise.resolve();
        }
        return this.firmwareRolloutService.getStudioSummary(this.studioId)
            .then(summary => {
                this.studioSummary = summary;
            })
            .catch(() => {
                // Silently ignored - the active/completed lists already surface errors.
            });
    }

    private loadActiveRollouts(silent: boolean = false): Promise<void> {
        if (!silent) {
            this.loadingActive = true;
        }
        return this.firmwareRolloutService.getRollouts(
            0, ACTIVE_ROLLOUTS_PAGE_SIZE, this.studioId ?? undefined, ACTIVE_STATUSES)
            .then((result: Page<FirmwareRolloutDTO>) => {
                this.activeRollouts = result.content;
            })
            .catch((error: any) => {
                if (!silent) {
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: error.message });
                }
            })
            .finally(() => {
                this.loadingActive = false;
            });
    }

    private loadCompletedRollouts(silent: boolean = false): Promise<void> {
        if (!silent) {
            this.loadingCompleted = true;
        }
        return this.firmwareRolloutService.getRollouts(
            this.currentPage, this.rows, this.studioId ?? undefined, COMPLETED_STATUSES)
            .then((result: Page<FirmwareRolloutDTO>) => {
                this.completedRollouts = result.content;
                this.totalCompleted = result.page.totalElements;
            })
            .catch((error: any) => {
                if (!silent) {
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: error.message });
                }
            })
            .finally(() => {
                this.loadingCompleted = false;
            });
    }

}
